import React from 'react';
import { Container, Paper, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta('Terms of Service — Seriously', 'Terms of service for Seriously, the voice-first dating app. Eligibility, account security, acceptable use, and user rights.');

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Terms of Service
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>1. Eligibility</Typography>
          <Typography variant="body1" paragraph>
            You must be at least 18 years old to use Seriously. By creating an account, you confirm that you are at least 18 years of age. We reserve the right to terminate accounts that we reasonably believe belong to minors.
          </Typography>

          <Typography variant="h6" gutterBottom>2. Account and Security</Typography>
          <Typography variant="body1" paragraph>
            Your account is accessed via a private URL and password. You are responsible for keeping these credentials secure. There is no password recovery mechanism. If you lose your credentials, your account cannot be recovered. We are not liable for unauthorized access resulting from your failure to protect your credentials.
          </Typography>

          <Typography variant="h6" gutterBottom>3. Acceptable Use</Typography>
          <Typography variant="body1" paragraph>
            You agree not to:
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>Upload photos that are not of yourself, or that depict minors.</li>
            <li>Upload content that is illegal, sexually explicit, violent, hateful, or discriminatory.</li>
            <li>Harass, threaten, or abuse other users through voice messages or any other feature.</li>
            <li>Impersonate another person or misrepresent your identity.</li>
            <li>Use the service for commercial purposes, spam, or solicitation.</li>
            <li>Attempt to access other users' accounts, data, or private information.</li>
            <li>Use automated tools, bots, or scripts to interact with the service.</li>
            <li>Circumvent security measures, rate limits, or access controls.</li>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>4. Content Standards</Typography>
          <Typography variant="body1" paragraph>
            Profile photos must be a clear photo of your face. Voice recordings must be your own voice. Tags must accurately describe you. We reserve the right to remove content that violates these standards without notice.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>5. Reporting and Moderation</Typography>
          <Typography variant="body1" paragraph>
            You can report profiles that violate these terms. Reports are reviewed by our team. We may suspend or permanently ban accounts that violate our terms. Banned accounts and all associated data will be permanently deleted.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>6. Biometric Data</Typography>
          <Typography variant="body1" paragraph>
            We process your photo and voice recording to generate mathematical embeddings (numerical vectors) used for compatibility matching. This constitutes biometric data processing under GDPR. You provide explicit consent for this processing at account creation. You may withdraw consent at any time by deleting your account.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>7. Intellectual Property</Typography>
          <Typography variant="body1" paragraph>
            You retain ownership of content you upload. By uploading content, you grant us a limited, non-exclusive license to use, display, and process it for the purpose of providing the service. This license terminates when you delete your account.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>8. Limitation of Liability</Typography>
          <Typography variant="body1" paragraph>
            The service is provided "as is" without warranties of any kind. We are not liable for any damages arising from your use of the service, including but not limited to interactions with other users. We do not conduct background checks on users.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>9. Account Deletion</Typography>
          <Typography variant="body1" paragraph>
            You may delete your account at any time from your Profile page. Deletion is permanent and irreversible. All your data, including photos, voice recordings, interactions, and messages, will be permanently removed.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>10. Termination</Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to suspend or terminate your account at any time, for any reason, including violation of these terms. We may also terminate the service entirely at our discretion.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>11. Changes to These Terms</Typography>
          <Typography variant="body1" paragraph>
            We may update these Terms of Service from time to time. Continued use of the service after changes constitutes acceptance of the updated terms.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>12. Governing Law</Typography>
          <Typography variant="body1" paragraph>
            These terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsOfService;
